// src/pages/AdminInboxPage.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs } from "firebase/firestore";
import "./AdminInboxPage.css";
import { serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getUserRole } from "./authUtils";
import { notificationService } from '../utils/notificationService';

const AdminInboxPage = () => {
  const [message, setMessage] = useState("");
  const [targetUser, setTargetUser] = useState("all");
  const [users, setUsers] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const verificationIntervalRef = useRef(null);

  // Security: Continuous admin verification
  useEffect(() => {
    const verifyAdminAccess = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (!user) {
          window.location.href = '/HomeScreen';
          return;
        }

        // Force token refresh
        await user.getIdToken(true);
        
        // Verify admin role
        const role = await getUserRole(user.uid);
        if (role !== 'admin') {
          console.warn('Unauthorized access attempt detected');
          setAttempts(prev => prev + 1);
          
          if (attempts >= 2) {
            // Too many failed attempts
            await auth.signOut();
            window.location.href = '/';
            return;
          }
          
          window.location.href = '/HomeScreen';
          return;
        }

        setIsVerified(true);
      } catch (error) {
        console.error('Admin verification failed:', error);
        window.location.href = '/HomeScreen';
      }
    };

    // Initial verification
    verifyAdminAccess();

    // Verify every 3 minutes
    verificationIntervalRef.current = setInterval(verifyAdminAccess, 3 * 60 * 1000);

    return () => {
      if (verificationIntervalRef.current) {
        clearInterval(verificationIntervalRef.current);
      }
    };
  }, [attempts]);

  useEffect(() => {
    if (!isVerified) return;

    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const usersList = querySnapshot.docs.map(doc => ({
          uid: doc.id,
          email: doc.data().email
        }));
        setUsers(usersList);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, [isVerified]);

  const handleSend = async () => {
    if (!isVerified) {
      notificationService.showWarning("Access denied. Please reload and verify admin credentials.");
      return;
    }

    if (!message.trim()) {
      notificationService.showWarning("Please enter a message.");
      return;
    }

    try {
      // Additional security check before sending
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        window.location.href = '/HomeScreen';
        return;
      }

      const role = await getUserRole(user.uid);
      if (role !== 'admin') {
        notificationService.showError("Admin verification failed. Redirecting...");
        window.location.href = '/HomeScreen';
        return;
      }

      await addDoc(collection(db, "messages"), {
        to: targetUser,
        from: "admin",
        content: message,
        timestamp: serverTimestamp(),
        adminId: user.uid, // Track which admin sent the message
      });

      setMessage("");
      notificationService.showSuccess("Message sent successfully!");
    } catch (error) {
      console.error("Failed to send message:", error);
      notificationService.showError("Failed to send message. Please try again.");
    }
  };

  // Show loading state while verifying admin access
  if (!isVerified) {
    return (
      <div className="admin-container">
        <div className="security-check">
          <h2>🔒 Verifying Admin Access</h2>
          <p>Please wait while we verify your administrative privileges...</p>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="security-header">
        <h2>🛡️ Secure Admin Inbox Panel</h2>
        <div className="security-status">
          <span className="status-indicator">●</span>
          <span>Secure Session Active</span>
        </div>
      </div>
      
      <div className="admin-content">
        <textarea
          placeholder="Type your administrative message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="secure-textarea"
        />

        <div className="send-options">
          <select 
            onChange={(e) => setTargetUser(e.target.value)}
            className="secure-select"
          >
            <option value="all">Send to All Users</option>
            {users.map((user) => (
              <option key={user.uid} value={user.uid}>{user.email}</option>
            ))}
          </select>
          
          <button onClick={handleSend} className="secure-send-btn">
            🔐 Send Secure Message
          </button>
        </div>
      </div>

      <div className="security-footer">
        <small>⚠️ This is a secure admin interface. All actions are logged and monitored.</small>
      </div>
    </div>
  );
};

export default AdminInboxPage;
